<template>
  <component
    :is="rootTag"
    ref="rootEl"
    data-form-card
    :class="[componentClass, componentBorderClass, darkBg, { 'form-editor': form }]"
    :style="componentStyle"
    @submit="handleSubmit"
    @invalid.capture.prevent="handleInvalid"
  >
    <header
      v-if="$slots.header"
      :class="[
        headerBorder,
        headerFooterBorder,
        collapsible ? collapsibleHeaderClass : '',
        headerPadding,
      ]"
      @click.prevent="handleHeaderClick"
    >
      <slot name="header" :collapsed="isCollapsed" :icon="computedHeaderIcon" />
    </header>
    <header
      v-else-if="title"
      class="flex items-stretch"
      :class="[headerBorder, headerFooterBorder, collapsible ? 'cursor-pointer' : '']"
      @click.prevent="handleHeaderClick"
    >
      <div
        class="flex min-w-0 flex-grow items-center py-3"
        :class="titlePadding"
      >
        <icon v-if="icon" :path="icon" class="mr-3" />
        <div class="min-w-0">
          <h3
            class="form-card__title flex items-center text-theme-text"
            :class="{ 'form-card__title--compact': compactHeader }"
          >
            {{ title }}
            <app-badge v-if="isPro" tone="success" label="Pro" class="ml-2" />
          </h3>
          <!-- `subtitle` was accepted at four call sites and rendered at none:
               with no prop declared it fell through as a plain HTML attribute,
               so two lines of explanation written for the grid-tariff and
               congestion cards have never reached a screen. -->
          <p v-if="subtitle" class="mt-0.5 text-xs text-theme-text-secondary">
            {{ subtitle }}
          </p>
        </div>
      </div>
      <a
        v-if="computedHeaderIcon"
        class="flex items-center py-3 px-4 justify-center"
        :class="headerIconDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
        @click.prevent="handleHeaderIconClick"
      >
        <icon :path="computedHeaderIcon" />
      </a>
      <a
        v-else-if="sourceHeader"
        :href="sourceHeaderUrl"
        target="_blank"
        class="flex items-center py-3 px-4 justify-center text-xs"
      >
        {{ sourceHeader }}
      </a>
    </header>

    <div
      :class="[
        isCollapsed ? collapsedClass : '',
        contentPadding,
        { 'flex-1': flex || flexRow || $slots.footer },
        contentClass,
      ]"
    >
      <div v-if="validationErrors.length" ref="errorSummary" class="form-error-summary" role="alert" tabindex="-1">
        <p class="font-semibold">{{ $t('forms.error_summary') }}</p>
        <ul>
          <li v-for="error in validationErrors" :key="error.id">
            <a :href="`#${error.id}`" @click.prevent="focusError(error)">{{ error.label }}: {{ error.message }}</a>
          </li>
        </ul>
      </div>
      <slot />
    </div>

    <div
      v-if="$slots.footer"
      :class="[footerWithoutBorder ? '' : headerFooterBorder, isCollapsed ? collapsedClass : '']"
      class="p-2 px-4 border-t flex justify-between flex-wrap"
    >
      <slot name="footer" />
    </div>
  </component>
</template>

<script setup>
  /* eslint-disable no-undef */
  import { computed, nextTick, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { getFormControlId } from '@/composables/useFormControlStyles';
  import { mdiChevronUp, mdiChevronDown } from '@mdi/js';
  import Icon from '@/components/Icon.vue';
  import AppBadge from '@/components/ui/feedback/AppBadge.vue';

  const props = defineProps({
    darkBg: {
      type: String,
      default: 'dark:bg-theme-surface dark:border-theme-border',
    },
    title: String,
    subtitle: String,
    icon: String,
    headerIcon: String,
    headerIconDisabled: Boolean,
    hasTable: Boolean,
    smallerPadding: Boolean,
    compactHeader: Boolean,
    cardWithoutBorder: Boolean,
    headerWithoutBorder: Boolean,
    footerWithoutBorder: Boolean,
    flex: Boolean,
    flexRow: Boolean,
    form: Boolean,
    hoverable: Boolean,
    collapsible: Boolean,
    isPro: Boolean,
    sourceHeader: String,
    sourceHeaderUrl: String,
    collapsibleHeaderClass: {
      type: String,
      default: 'cursor-pointer',
    },
    collapsed: {
      type: Boolean,
      default: null,
    },
    collapsedClass: {
      type: String,
      default: 'hidden',
    },
    class: String,
    contentClass: String,
    rounded: {
      type: String,
      default: 'rounded-theme-md',
    },
    noPadding: Boolean,
    overflowVisible: Boolean,
  });

  const emit = defineEmits(['header-icon-click', 'submit', 'update:collapsed']);
  const { t } = useI18n();
  const rootEl = ref(null);
  const errorSummary = ref(null);
  const validationErrors = ref([]);
  let summaryScheduled = false;

  function handleInvalid(event) {
    // A native form owns its summary; standalone nested cards own theirs.
    const owner = validationOwner(event.target);
    if (owner === rootEl.value) scheduleErrorSummary();
  }

  function validationOwner(control) {
    return control.closest('form[data-form-card]') || control.closest('[data-form-card]');
  }

  function invalidControls() {
    return [...(rootEl.value?.querySelectorAll('input, select, textarea') || [])]
      .filter(control => control.willValidate && !control.validity.valid);
  }

  async function scheduleErrorSummary() {
    if (summaryScheduled) return;
    summaryScheduled = true;
    if (isCollapsed.value) {
      internalCollapsed.value = false;
      emit('update:collapsed', false);
    }
    await nextTick();
    validationErrors.value = invalidControls().filter(control => validationOwner(control) === rootEl.value).map(control => {
      if (!control.id) control.id = getFormControlId('form-error');
      return {
        id: control.id,
        label: control.labels?.[0]?.textContent.trim() || control.getAttribute('aria-label') || control.name || t('forms.field'),
        message: control.validationMessage,
      };
    });
    await nextTick();
    errorSummary.value?.focus();
    summaryScheduled = false;
  }

  function focusError(error) {
    const control = [...rootEl.value.querySelectorAll('input, select, textarea')].find(input => input.id === error.id);
    if (!control) return;
    for (let parent = control.parentElement; parent; parent = parent.parentElement) {
      if (parent.tagName === 'DETAILS') parent.open = true;
    }
    control.focus();
    control.scrollIntoView?.({ block: 'nearest' });
  }

  function validate() {
    const controls = invalidControls();
    for (const control of controls) control.checkValidity();
    if (controls.some(control => validationOwner(control) === rootEl.value)) scheduleErrorSummary();
    else validationErrors.value = [];
    return controls.length === 0;
  }

  defineExpose({ validate });

  const internalCollapsed = ref(true);
  const collapsedState = computed(() =>
    props.collapsed === null ? internalCollapsed.value : props.collapsed
  );
  const isCollapsed = computed(() => props.collapsible && collapsedState.value);

  const headerPadding = computed(() => {
    if (props.compactHeader) return 'px-4 py-3';
    return !props.hasTable && !props.smallerPadding ? 'p-6' : 'py-6 px-4';
  });
  const titlePadding = computed(() => (props.smallerPadding || props.icon ? 'px-4' : 'px-6'));
  const contentPadding = computed(() => {
    if (props.noPadding) return '';
    return !props.hasTable && !props.smallerPadding ? 'p-6' : 'py-6 px-4';
  });

  const headerBorder = computed(() => (props.headerWithoutBorder ? '' : 'border-b'));
  const headerFooterBorder = 'border-theme-border dark:border-theme-border';

  const rootTag = computed(() => (props.form ? 'form' : 'div'));

  const componentBorderClass = computed(() => {
    const base = 'bg-theme-surface border border-theme-border';
    if (props.cardWithoutBorder) return 'bg-theme-surface';
    return base;
  });

  const componentClass = computed(() => {
    const base = [props.rounded];

    if (props.class) base.push(props.class);

    if (props.flexRow) base.push('flex flex-row');
    else if (props.flex) base.push('flex flex-col');
    else base.push('block');

    if (props.hoverable) base.push('hover:shadow-theme-card dark:hover:shadow-theme-shadow/50 transition-shadow duration-500');

    return base;
  });

  const componentStyle = computed(() => ({
    overflow: props.overflowVisible ? 'visible' : 'hidden',
  }));

  const computedHeaderIcon = computed(() => {
    if (props.headerIcon) return props.headerIcon;
    if (props.collapsible) return collapsedState.value ? mdiChevronDown : mdiChevronUp;
    return null;
  });

  const handleHeaderClick = () => {
    if (!props.collapsible) return;
    const next = !collapsedState.value;
    if (props.collapsed === null) internalCollapsed.value = next;
    emit('update:collapsed', next);
  };

  const handleHeaderIconClick = (e) => {
    if (props.headerIconDisabled) return;
    emit('header-icon-click', e);
  };

  const handleSubmit = (e) => {
    if (!validate()) {
      e.preventDefault();
      return;
    }
    emit('submit', e);
  };
</script>
